import { useCallback, useEffect, useMemo, useState } from "react";
import { isArray } from "lodash";
import fp from "lodash/fp";
import { UseMutateAsyncFunction } from "react-query";

import {
	useAddTerminalSolutionByApplicationMutation,
	useRemoveTerminalSolutionForApplicationMutation,
	useUpdateTerminalSolutionByApplicationMutation,
	getDefaultValuesTerminalSolution,
} from "queries";
import { ErrorResponse, TerminalSolutionTypes } from "types";
import {
	TerminalSolutionCreateDto,
	TerminalSolutionEditDto,
	TerminalSolutionGatewayDto,
	TerminalSolutionPointOfSaleDto,
	TerminalSolutionTerminalEditDto,
} from "types/api";
import { TerminalSolutionTypesKeys } from "types/enums/terminalSolutionTypes";
import { float6Normalizer } from "validators";

import {
	getAutoCloseAt,
	getBackEndPlatform,
	getFileBuildInfoForMerchant,
	getFrontEndPlatform,
	getGatewayInfo,
	getInstallationInfo,
	getPaymentMethods,
	getPointOfSaleInfo,
	getShippingData,
	getShippingDetails,
	getTerminalFieldsForApp,
	getTerminalInfo,
	getTerminalOptions,
	getZeroCostInfo,
	terminalCalculation,
} from "./terminalSolutionMappers";

type TerminalDefaultParams = {
	data?: any;
	origin?: string;
	merchantGeneralData?: any;
	applicationProcessor?: any;
};

type UseTerminalSolutionFormProps = {
	applicationId?: string;
	immediateSaveAppFn?: Function;
	initDefaultValues?: TerminalDefaultParams;
	setCalculationTerminalValue?: Function;
	terminalList?: { [key: string]: any };
};

export const useTerminalSolutionForm = ({
	applicationId,
	immediateSaveAppFn,
	initDefaultValues = {},
	setCalculationTerminalValue,
	terminalList,
}: UseTerminalSolutionFormProps = {}) => {
	const [gatewayCount, setGatewayCount] = useState<number>(0);
	const [terminalCount, setTerminalCount] = useState<number>(0);

	const terminalGatewayCount = useMemo(
		() => gatewayCount + terminalCount,
		[gatewayCount, terminalCount],
	);

	useEffect(() => {
		setGatewayCount(
			terminalList?.items?.reduce((count, item) => {
				return item.gateway !== null ? count + 1 : count;
			}, 0) ?? 0,
		);
		setTerminalCount(
			terminalList?.items?.reduce((count, item) => {
				return item.terminal !== null ? count + 1 : count;
			}, 0) ?? 0,
		);
	}, [terminalList?.items]);

	const useDefaultValues = ({
		data,
		origin,
		merchantGeneralData,
		applicationProcessor,
	}: TerminalDefaultParams) =>
		useMemo(
			() =>
				getDefaultValuesTerminalSolution({
					data,
					applicationProcessor,
					merchantGeneralData,
					origin,
				}),
			[data, applicationProcessor, merchantGeneralData, origin],
		);

	const terminalDefaultValues = useMemo(
		() => getDefaultValuesTerminalSolution(initDefaultValues),
		[initDefaultValues],
	);

	const { mutateAsync: createTerminalRequest } =
		useAddTerminalSolutionByApplicationMutation({
			applicationId,
		});

	const { mutateAsync: updateTerminalRequest } =
		useUpdateTerminalSolutionByApplicationMutation({
			applicationId,
		});

	const { mutateAsync: removeTerminalRequest } =
		useRemoveTerminalSolutionForApplicationMutation({ applicationId });

	const onCreateForMerchant = useCallback(
		(
			addTerminal: UseMutateAsyncFunction<
				any,
				ErrorResponse,
				TerminalSolutionCreateDto,
				unknown
			>,
		): any => {
			return async values => {
				const frontEndPlatforms = getFrontEndPlatform(values);
				const backEndPlatforms = getBackEndPlatform(values);
				const billings =
					values?.isPointOfSale || values?.isFileBuildOnly
						? []
						: getPaymentMethods(values?.paymentMethods);
				const shippingData = getShippingData(values?.shipping);
				const shippingDetails = getShippingDetails(values?.shipping);
				const fileBuildInfo = getFileBuildInfoForMerchant(
					values?.fileBuild,
					values?.backEndPlatform,
				);
				const installationInfo = getInstallationInfo(
					values?.installation,
				);
				const zeroCostInfo = getZeroCostInfo(values);

				let data: TerminalSolutionCreateDto = {
					origin: values.origin,
					status: values.status,
					tid_v_number: values.tidVNumber,
					is_file_build_only_solution_type: values.isFileBuildOnly,
					front_end_mid: values.frontEndMid,

					shipping: values.isTerminal
						? { ...shippingData, ...shippingDetails }
						: undefined,

					...frontEndPlatforms,
					...backEndPlatforms,
					...billings,
					...fileBuildInfo,
					...installationInfo,
					...zeroCostInfo,
				};
				let terminal: TerminalSolutionTerminalEditDto | undefined =
					undefined;
				let gateway: TerminalSolutionGatewayDto | undefined = undefined;
				let pointOfSale: TerminalSolutionPointOfSaleDto | undefined =
					undefined;

				if (values.isTerminal) {
					const terminalOptions = getTerminalOptions(
						values?.terminal?.terminalOptions,
					);
					const autoCloseAt = getAutoCloseAt(values?.terminal);

					const terminalInfo = getTerminalInfo({
						...values?.terminal,
						terminalAccessories: values?.terminalAccessories,
					});

					terminal = {
						...terminalOptions,
						...autoCloseAt,
						...terminalInfo,
					};
				}

				if (values.isGateway) {
					const gatewayInfo = getGatewayInfo(values?.gateway);
					gateway = {
						...gatewayInfo,
					};
				}
				if (values.isPointOfSale) {
					const pointOfSaleInfo = getPointOfSaleInfo(
						values?.pointOfSale,
					);
					pointOfSale = {
						...pointOfSaleInfo,
					};
				}
				data = {
					...data,
					terminal,
					gateway,
					point_of_sale: pointOfSale,
				};

				return await addTerminal(data);
			};
		},
		[],
	);

	const onUpdateForMerchant = useCallback(
		(
			updateTerminal: UseMutateAsyncFunction<
				any,
				ErrorResponse,
				TerminalSolutionEditDto,
				unknown
			>,
		): any => {
			return async values => {
				const update = onCreateForMerchant(async data => {
					return await updateTerminal({
						terminal_program_type: values.terminalProgramType.value,
						...data,
					});
				});
				await update(values);
			};
		},
		[onCreateForMerchant],
	);

	const createTerminalForApp = useCallback(
		async ({ terminalSolutionType }) => {
			let appData:
				| { applicationId: string; error: string | null }
				| undefined = undefined;

			if (!applicationId) {
				try {
					appData = await immediateSaveAppFn?.();
				} catch (e) {
					return Promise.reject(e);
				}

				if (!appData?.applicationId) {
					return Promise.reject();
				}
			}

			const mappedData = getTerminalFieldsForApp({
				...terminalDefaultValues,
				status: "draft",
				terminalSolutionType,
			});

			delete mappedData.id;

			return createTerminalRequest(mappedData).then(terminalData => {
				if (
					terminalSolutionType === TerminalSolutionTypes.Terminal.name
				) {
					setTerminalCount(terminalCount + 1);
				}
				if (
					terminalSolutionType === TerminalSolutionTypes.Gateway.name
				) {
					setGatewayCount(gatewayCount + 1);
				}
				return getDefaultValuesTerminalSolution({ data: terminalData });
			});
		},
		[
			applicationId,
			createTerminalRequest,
			gatewayCount,
			immediateSaveAppFn,
			terminalCount,
			terminalDefaultValues,
		],
	);

	const updateTerminalForApp = useCallback(
		({
			getValues,
			terminalSolutionType,
			collector,
			prevValues,
			setPrevValues,
		}: {
			getValues: Function;
			terminalSolutionType?: TerminalSolutionTypesKeys;
			collector?: Function;
			prevValues?:
				| { [key: string]: any }
				| { [key: string]: any }[]
				| undefined;
			setPrevValues?: Function;
		}) => {
			const formValues = getValues();
			collector?.(formValues);

			const sendRequest = (
				dataToSend: { [key: string]: any },
				index?: number,
			) => {
				const prevValuesSelector =
					index === undefined ? prevValues : prevValues?.[index];

				if (dataToSend?.id) {
					if (fp.isEqual(prevValuesSelector, dataToSend)) {
						return Promise.resolve();
					}

					const mappedData = getTerminalFieldsForApp({
						terminalSolutionType,
						...dataToSend,
					});

					return updateTerminalRequest({
						...mappedData,
						status: "draft",
					});
				}

				return Promise.resolve();
			};

			if (Array.isArray(formValues)) {
				return Promise.all(
					formValues.map((values, index) =>
						sendRequest(values, index),
					),
				).then(() => {
					setPrevValues?.(formValues);
				});
			} else {
				return sendRequest(formValues).then(() => {
					setPrevValues?.(formValues);
				});
			}
		},
		[updateTerminalRequest],
	);

	const removeTerminalForApp = useCallback(
		terminalId => {
			if (!applicationId || !terminalId) {
				return Promise.reject();
			}

			return removeTerminalRequest({ terminalId });
		},
		[applicationId, removeTerminalRequest],
	);

	const calculateAmountToBeBill = useCallback(
		formValues => {
			if (isArray(formValues)) {
				const calculation = terminalCalculation(formValues);
				setCalculationTerminalValue?.(float6Normalizer(calculation));
			}
		},
		[setCalculationTerminalValue],
	);

	const updateTerminalGeneralInfoForApp = useCallback(
		({
			getValues,
			collector,
		}: {
			getValues: Function;
			collector?: Function;
		}) => {
			collector?.(getValues());
			return Promise.resolve();
		},
		[],
	);

	return {
		createTerminalForApp,
		updateTerminalForApp,
		removeTerminalForApp,
		calculateAmountToBeBill,
		updateTerminalGeneralInfoForApp,
		onCreateForMerchant,
		onUpdateForMerchant,
		useDefaultValues,
		terminalDefaultValues,
		terminalGatewayCount,
		terminalCount,
		gatewayCount,
		setGatewayCount,
		setTerminalCount,
	};
};
